import React, { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import useOutsideInteraction from "../Sidebar/hooks/useOutsideInteraction";
import Avatar from "../Sidebar/components/Avatar";
import useMerchantSelection from "../../api/useMerchantSelection";
import MerchantDropdownItem from "./MerchantDropdownItem";

function getCurrentEntityTypeLabel(session) {
  if (session.entityType === "LEGALENTITY") {
    return "Main Account";
  }
  return "Sub-Merchant";
}

export default function MerchantSwitcher({ session }) {
  const [open, setOpen] = useState(false);
  const switcherRef = useRef(null);
  const { selectEntity } = useMerchantSelection();

  useOutsideInteraction(switcherRef, open, () => setOpen(false));

  const mainAccounts = useMemo(() => session.legalEntities || [], [session.legalEntities]);
  const subMerchants = useMemo(() => session.merchants || [], [session.merchants]);

  const activeLegalEntityId = useMemo(() => {
    if (session.entityType === "LEGALENTITY") return session.entityId;
    return session.legalEntityId;
  }, [session.entityId, session.entityType, session.legalEntityId]);

  const filteredSubMerchants = useMemo(() => {
    if (!Array.isArray(subMerchants) || !subMerchants.length) return [];
    const hasRelationKey = subMerchants.some(
      (merchant) => merchant?.LegalEntityID !== undefined,
    );

    if (!hasRelationKey) return subMerchants;

    return subMerchants.filter(
      (merchant) => Number(merchant?.LegalEntityID) === Number(activeLegalEntityId),
    );
  }, [activeLegalEntityId, subMerchants]);

  const toggleOpen = () => setOpen((value) => !value);

  const handleSelect = async (entity, selected) => {
    if (selected) return;
    setOpen(false);
    await selectEntity(entity);
  };

  const isMainEntitySelected = (entity) =>
    session.entityType === "LEGALENTITY" &&
    Number(entity?.LegalEntityID) === Number(session.entityId);

  const isSubMerchantSelected = (merchant) =>
    session.entityType !== "LEGALENTITY" &&
    Number(merchant?.MerchantID) === Number(session.entityId);

  return (
    <div className="navbar-switcher" ref={switcherRef}>
      <button
        type="button"
        className="navbar-switcher__trigger"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <Avatar
          src={session.entityImageUrl}
          alt={session.entityName}
          className="navbar-switcher__avatar"
          fit="contain"
        />
        <span className="navbar-switcher__content">
          <strong>{session.entityName}</strong>
          <small>{getCurrentEntityTypeLabel(session)}</small>
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`navbar-switcher__chevron ${open ? "is-open" : ""}`}
        />
      </button>

      {open ? (
        <div className="navbar-switcher__menu">
          {mainAccounts.length ? (
            <div className="navbar-switcher__section">
              <div className="navbar-switcher__section-title">Main Account</div>
              <div className="navbar-switcher__list">
                {mainAccounts.map((entity) => {
                  const selected = isMainEntitySelected(entity);

                  return (
                    <MerchantDropdownItem
                      key={entity?.LegalEntityID}
                      name={entity?.LegalEntityName || "Main Account"}
                      subtitle="Main Account"
                      imageUrl={entity?.LegalEntityIconUrl || ""}
                      fit="contain"
                      selected={selected}
                      onClick={() => handleSelect(entity, selected)}
                    />
                  );
                })}
              </div>
            </div>
          ) : null}

          {filteredSubMerchants.length ? (
            <div className="navbar-switcher__section">
              <div className="navbar-switcher__section-title">Sub-Merchants</div>
              <div className="navbar-switcher__list">
                {filteredSubMerchants.map((merchant) => {
                  const isSelected = isSubMerchantSelected(merchant);

                  return (
                    <MerchantDropdownItem
                      key={merchant?.MerchantID}
                      name={merchant?.MerchantName}
                      subtitle="Sub-Merchant"
                      imageUrl={merchant?.MerchantIconURL || merchant?.MerchantIconUrl}
                      fit="contain"
                      selected={isSelected}
                      onClick={() => handleSelect(merchant, isSelected)}
                    />
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
